import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import { LUX_LOYALTY_PROGRAM_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import { ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import canShowTab from '~/utils/canShowTab';

import LoyaltyClaimCampaignsPage from './LoyaltyClaimCampaignsPage';
import LoyaltyCustomRulePage from './LoyaltyCustomRulePage';
import LoyaltyManualAccrualsPage from './LoyaltyManualAccrualsPage';
import LoyaltyOperationsPage from './LoyaltyOperationsPage';
import LoyaltyPromoCampaignsPage from './LoyaltyPromoCampaignsPage';
import LoyaltyUserClaimsPage from './LoyaltyUserClaimsPage';

const LOYALTY_ADMIN_ROLES = [ROLE_LUX_LOYALTY_ADMIN];

interface TabConfig {
  label: string;
  slug: string;
  component: React.ComponentType;
  requiredRoles?: Array<string>;
}

export const TabsMap: Record<string, TabConfig> = {
  'custom-rule': {
    label: 'Custom Rule',
    slug: 'custom-rule',
    component: LoyaltyCustomRulePage,
  },
  'manual-accruals': {
    label: 'Manual Accruals',
    slug: 'manual-accruals',
    component: LoyaltyManualAccrualsPage,
  },
  'promo-campaigns': {
    label: 'Promo Campaigns',
    slug: 'promo-campaigns',
    component: LoyaltyPromoCampaignsPage,
  },
  'claim-campaigns': {
    label: 'Claim Campaigns',
    slug: 'claim-campaigns',
    component: LoyaltyClaimCampaignsPage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  'customer-operations': {
    label: 'Customer Operations',
    slug: 'customer-operations',
    component: LoyaltyOperationsPage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
  'customer-claims': {
    label: 'Process Campaign Redemptions',
    slug: 'customer-claims',
    component: LoyaltyUserClaimsPage,
    requiredRoles: LOYALTY_ADMIN_ROLES,
  },
} as const;

type TabKey = keyof typeof TabsMap;

preRegisterSpotlightRoutes(expandTabRoutes('/societe', 'Societe', TabsMap));

export default function LoyaltyPageContainer() {
  const { section: rawSection } = useParams<{ section?: TabKey }>();
  const { user } = useCurrentUser();

  const programName = LUX_LOYALTY_PROGRAM_CONFIG.programDisplayName;

  const visibleTabs = Object.keys(TabsMap).filter((tab) => {
    const { requiredRoles } = TabsMap[tab];
    if (!requiredRoles) return true;
    return canShowTab(user, requiredRoles);
  });

  const currentTab = TabsMap[rawSection];
  const isTabVisible = visibleTabs.includes(rawSection);
  const CurrentComponent = isTabVisible ? currentTab?.component : undefined;

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{programName}</title>
      </Helmet>

      <PageHeader title={programName} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={isTabVisible ? rawSection : false} variant="scrollable" scrollButtons="auto">
          {visibleTabs.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={TabsMap[tab].label}
              to={`/societe/${tab}`}
              component={Link}
              sx={{
                minWidth: 30,
                padding: '12px 10px',
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box mt={4}>{CurrentComponent && <CurrentComponent />}</Box>
    </Container>
  );
}
